import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './WorkshopMigrationStatus.css';

function WorkshopMigrationStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [validating, setValidating] = useState(false);
  const [showRunConfirm, setShowRunConfirm] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/workshops/migration/status');
      setStatus(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch migration status:', err);
      setError('Failed to load migration status');
    } finally {
      setLoading(false);
    }
  };

  const handleRunMigration = async () => {
    try {
      setRunning(true);
      setShowRunConfirm(false);
      await axios.post('/api/workshops/migration/run');
      await fetchStatus();
    } catch (err) {
      console.error('Failed to run migration:', err);
      alert('Migration failed. Check error log for details.');
      await fetchStatus();
    } finally {
      setRunning(false);
    }
  };

  const handleValidate = async () => {
    try {
      setValidating(true);
      const response = await axios.post('/api/workshops/migration/validate');
      setValidationResult(response.data);
    } catch (err) {
      console.error('Failed to validate migration:', err);
      alert('Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="workshop-migration-status">
        <div className="ws-loading">
          <div className="spinner"></div>
          <p>Loading migration status...</p>
        </div>
      </div>
    );
  }

  const stats = status?.summary || {};
  const errors = status?.errors || [];
  const total = (stats.processed || 0);
  const progressPercent = total > 0 ? Math.round(((stats.successful || 0) / total) * 100) : 0;

  return (
    <div className="workshop-migration-status">
      <div className="ws-page-header">
        <h1>Shopify Migration</h1>
        <div className="ws-header-actions">
          <button
            className="ws-btn-secondary"
            onClick={handleValidate}
            disabled={validating}
          >
            {validating ? 'Validating...' : 'Validate Migration'}
          </button>
          <button
            className="ws-btn-primary"
            onClick={() => setShowRunConfirm(true)}
            disabled={running}
          >
            {running ? 'Running...' : 'Run Migration'}
          </button>
        </div>
      </div>

      {error && <div className="ws-error">{error}</div>}

      {showRunConfirm && (
        <div className="ws-confirm-banner">
          <p>This will import workshop orders from Shopify into the scheduling system. Continue?</p>
          <div className="ws-confirm-actions">
            <button className="ws-btn-primary ws-btn-sm" onClick={handleRunMigration}>
              Yes, Run Migration
            </button>
            <button className="ws-btn-secondary ws-btn-sm" onClick={() => setShowRunConfirm(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="ws-stat-cards">
        <div className="ws-stat-card">
          <div className="ws-stat-label">Processed</div>
          <div className="ws-stat-value">{stats.processed || 0}</div>
        </div>
        <div className="ws-stat-card">
          <div className="ws-stat-label">Successful</div>
          <div className="ws-stat-value ws-stat-success">{stats.successful || 0}</div>
        </div>
        <div className="ws-stat-card">
          <div className="ws-stat-label">Errors</div>
          <div className="ws-stat-value ws-stat-error">{stats.errors || 0}</div>
        </div>
        <div className="ws-stat-card">
          <div className="ws-stat-label">Skipped</div>
          <div className="ws-stat-value">{stats.skipped || 0}</div>
        </div>
      </div>

      {total > 0 && (
        <div className="ws-section">
          <h2>Progress</h2>
          <div className="ws-migration-progress">
            <div className="ws-migration-bar">
              <div
                className="ws-migration-fill"
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
            <span className="ws-migration-percent">{progressPercent}% complete</span>
          </div>
          {status?.last_run && (
            <div className="ws-migration-meta">
              Last run: {formatDate(status.last_run)}
            </div>
          )}
        </div>
      )}

      {validationResult && (
        <div className="ws-section">
          <h2>Validation Results</h2>
          <div className="ws-validation-summary">
            <div className="ws-validation-item">
              <span className="ws-validation-label">Total Records</span>
              <span>{validationResult.total || 0}</span>
            </div>
            <div className="ws-validation-item">
              <span className="ws-validation-label">Valid</span>
              <span className="ws-stat-success">{validationResult.valid || 0}</span>
            </div>
            <div className="ws-validation-item">
              <span className="ws-validation-label">Issues</span>
              <span className="ws-stat-error">{validationResult.issues || 0}</span>
            </div>
          </div>
          {(validationResult.details || []).length > 0 && (
            <div className="ws-table-container" style={{ marginTop: '16px' }}>
              <table className="ws-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Message</th>
                    <th>Record</th>
                  </tr>
                </thead>
                <tbody>
                  {validationResult.details.map((detail, index) => (
                    <tr key={index}>
                      <td>
                        <span className={`ws-badge ${detail.type === 'error' ? 'ws-badge-cancelled' : 'ws-badge-pending'}`}>
                          {detail.type}
                        </span>
                      </td>
                      <td>{detail.message}</td>
                      <td className="ws-mono">{detail.record_id || '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <button
            className="ws-btn-secondary ws-btn-sm"
            style={{ marginTop: '12px' }}
            onClick={() => setValidationResult(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="ws-section">
        <h2>Error Log</h2>
        {errors.length === 0 ? (
          <div className="ws-empty">No migration errors</div>
        ) : (
          <div className="ws-table-container">
            <table className="ws-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Error</th>
                  <th>Raw Data</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {errors.map((err, index) => (
                  <tr key={index}>
                    <td className="ws-mono">{err.source || '--'}</td>
                    <td className="ws-error-text">{err.error_message}</td>
                    <td>
                      {err.raw_data && (
                        <details className="ws-raw-data">
                          <summary>View</summary>
                          <pre>{JSON.stringify(err.raw_data, null, 2)}</pre>
                        </details>
                      )}
                    </td>
                    <td>{formatDate(err.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkshopMigrationStatus;
